import React, { useMemo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Table, TableHead, TableBody, TableCell, TableContainer, TableRow,
  Paper, Box, Typography, Select, MenuItem, Stack, Pagination,
} from "@mui/material";
import { getCoverageListingAction } from "../../../redux/actions/policyActions";

const normalizeType = (s) => String(s ?? "").toLowerCase();

const asNumberOrZero = (v) => {
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
};

const displayMoney = (v) => {
  if (v === null || v === undefined || v === "") return "-";
  const n = Number(v);
  if (Number.isNaN(n)) return "-";
  return n.toFixed(2);
};

const PolicyCoveragesListing = ({ policyId }) => {
  const dispatch = useDispatch();

  // Get data from Redux
  const premiumVehicles = useSelector((s) => s.policy.coverages);
  const storedPolicyId = useSelector((s) => s.policy.allDetailPolicy?.id ?? null);
  const effectivePolicyId = policyId ?? storedPolicyId;

  // Extract vehicles and pagination info
  const vehicleRows = premiumVehicles?.content ?? [];
  const totalVehicles = premiumVehicles?.totalElements ?? 0;
  const totalPages = premiumVehicles?.totalPages ?? 1;
  const currentBackendPage = premiumVehicles?.number ?? 0;

  // Local state
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Initial fetch
  useEffect(() => {
    if (!effectivePolicyId) return;
    dispatch(getCoverageListingAction(effectivePolicyId, 0, rowsPerPage));
  }, [effectivePolicyId]);

  // Calculate header labels - these show the limit values for each coverage type
  const headerLabels = useMemo(() => {
    const agg = (type, formatter) => {
      const values = new Set();
      let sawAny = false;
      let onlyNoCovg = true;

      for (const vehicle of vehicleRows) {
        const coverages = vehicle?.coverages?.content ?? [];
        const cov = coverages.find((c) => normalizeType(c?.type) === type);
        if (!cov) continue;

        sawAny = true;
        const lt = String(cov?.limitType ?? "").toUpperCase();
        if (lt !== "NO_COVERAGE") {
          onlyNoCovg = false;
          const str = formatter(cov);
          if (str) values.add(str);
        }
      }

      if (!sawAny) return "-";
      if (onlyNoCovg) return "No Covg";
      if (values.size === 0) return "-";
      if (values.size === 1) return Array.from(values)[0];
      return "Mixed";
    };

    return {
      bi: agg("bi", (c) => (c?.limit1 && c?.limit2) ? `${c.limit1}/${c.limit2}` : null),
      pd: agg("pd", (c) => (c?.limit1 ? String(c.limit1) : null)),
      pip: agg("pip", (c) => (c?.limit1 ? String(c.limit1) : null)),
      comp: agg("comp", (c) => (c?.deductible ? String(c.deductible) : null)),
      coll: agg("coll", (c) => (c?.deductible ? String(c.deductible) : null)),
      umbi: agg("umbi", (c) => (c?.limit1 && c?.limit2) ? `${c.limit1}/${c.limit2}` : null),
      medpay: agg("medpay", (c) => (c?.limit1 ? String(c.limit1) : null)),
      tow: agg("tow", (c) => (c?.limit1 ? String(c.limit1) : null)),
      rental: agg("rental", (c) => (c?.limit1 ? String(c.limit1) : null)),
    };
  }, [vehicleRows]);

  // Build coverage map for a vehicle
  const buildCoverageIndex = (vehicle) => {
    const map = {};
    const coverages = vehicle?.coverages?.content ?? [];
    coverages.forEach((c) => {
      const key = normalizeType(c?.type);
      if (key) map[key] = c;
    });
    return map;
  };

  // Calculate vehicle row total
  const calculateVehicleTotal = (vehicle) => {
    const coverages = vehicle?.coverages?.content ?? [];
    const sum = coverages.reduce((acc, cov) => acc + asNumberOrZero(cov?.premium), 0);
    return sum.toFixed(2);
  };

  // Calculate grand total premium for current page
  const pageTotalPremium = useMemo(() => {
    const total = vehicleRows.reduce((sum, vehicle) => {
      const coverages = vehicle?.coverages?.content ?? [];
      const vehicleSum = coverages.reduce((sub, cov) => sub + asNumberOrZero(cov?.premium), 0);
      return sum + vehicleSum;
    }, 0);
    return total.toFixed(2);
  }, [vehicleRows]);

  // Handle page change
  const handleChangePage = (_event, newPage) => {
    if (!effectivePolicyId) return;
    const page0 = newPage - 1;
    dispatch(getCoverageListingAction(effectivePolicyId, page0, rowsPerPage));
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    if (!effectivePolicyId) return;
    const newSize = parseInt(event.target.value, 10);
    setRowsPerPage(newSize);
    dispatch(getCoverageListingAction(effectivePolicyId, 0, newSize));
  };

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center">No.</TableCell>
              <TableCell align="center">
                BI<br />({headerLabels.bi})
              </TableCell>
              <TableCell align="center">
                PD<br />({headerLabels.pd})
              </TableCell>
              <TableCell align="center">
                PIP<br />({headerLabels.pip})
              </TableCell>
              <TableCell align="center">
                COMP<br />
              </TableCell>
              <TableCell align="center">
                COL<br />
              </TableCell>
              <TableCell align="center">
                UM<br />({headerLabels.umbi})
              </TableCell>
              <TableCell align="center">
                MED<br />({headerLabels.medpay === 'NA' ? 'No Covg' : headerLabels.medpay})
              </TableCell>
              <TableCell align="center">
                TOW<br />({headerLabels.tow})
              </TableCell>
              <TableCell align="center">
                RENTAL<br />({headerLabels.rental})
              </TableCell>
              <TableCell align="center">OTHER</TableCell>
              <TableCell align="center">TOTAL</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {vehicleRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ py: 3 }}>
                  <Typography variant="body1" color="textSecondary">
                    No vehicles found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              vehicleRows.map((vehicle, index) => {
                const coverageMap = buildCoverageIndex(vehicle);
                const getCell = (typeKey) => displayMoney(coverageMap[typeKey]?.premium);
                const globalVehicleNumber = currentBackendPage * rowsPerPage + index + 1;

                return (
                  <TableRow
                    key={vehicle.id}
                  >
                    <TableCell align="center">#{globalVehicleNumber}</TableCell>
                    <TableCell align="center">{getCell("bi")}</TableCell>
                    <TableCell align="center">{getCell("pd")}</TableCell>
                    <TableCell align="center">{getCell("pip")}</TableCell>
                    <TableCell align="center">{getCell("comp")}</TableCell>
                    <TableCell align="center">{getCell("coll")}</TableCell>
                    <TableCell align="center">{getCell("umbi")}</TableCell>
                    <TableCell align="center">{getCell("medpay")}</TableCell>
                    <TableCell align="center">{getCell("tow")}</TableCell>
                    <TableCell align="center">{getCell("rental")}</TableCell>
                    <TableCell align="center">-</TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      {calculateVehicleTotal(vehicle)}
                    </TableCell>
                  </TableRow>
                );
              })
            )}

            {/* Grand Total Row */}
            {vehicleRows.length > 0 && (
              <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                <TableCell
                  colSpan={12}
                  align="right"
                  className="styled-table-row"
                  sx={{ fontWeight: "bold", pr: 2 }}
                >
                  Grand Total Premium: {pageTotalPremium}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalVehicles > 0 && (
        <Box sx={{ width: "100%", overflow: "hidden", padding: "16px 0" }}>
          <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
            <Box display="flex" alignItems="center">
              <Typography variant="body2" sx={{ mr: 1 }}>
                Rows per page:
              </Typography>
              <Select
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                size="small"
                sx={{
                  height: "36px",
                  minWidth: "80px",
                  "& .MuiSelect-select": { padding: "8px 32px 8px 12px" },
                }}
              >
                {[5,10, 15, 25, 50, 100].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Pagination
              count={totalPages}
              page={currentBackendPage + 1}
              onChange={handleChangePage}
              showFirstButton
              showLastButton
              siblingCount={1}
              boundaryCount={1}
              size="small"
            />
          </Stack>

          <Box sx={{ px: 2, pt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Showing vehicles {currentBackendPage * rowsPerPage + 1} -{" "}
              {Math.min((currentBackendPage + 1) * rowsPerPage, totalVehicles)} of{" "}
              {totalVehicles} total
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PolicyCoveragesListing;
